import React, { useState } from 'react';
import { Search, SlidersHorizontal, Plus, Briefcase } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import OpportunityCard from './OpportunityCard';
import OpportunityDetailModal from './OpportunityDetailModal';
import ApplyModal from './ApplyModal';
import PostOpportunityModal from './PostOpportunityModal';
import { useOpportunities } from '../hooks/use-opportunities';

const TYPES = ['All', 'Full Time', 'Part Time', 'Internship', 'Contract'];

// ── Skeleton Loader ────────────────────────────────────────────────────────────
const OpportunityCardSkeleton = () => (
  <div className="bg-white rounded-2xl border border-gray-200 p-8 shadow-sm animate-pulse">
    <div className="flex items-center gap-3 mb-4">
      <div className="h-6 bg-gray-200 rounded-lg w-64" />
      <div className="h-5 bg-gray-100 rounded-full w-20" />
    </div>
    <div className="flex gap-6 mb-6">
      <div className="h-4 bg-gray-100 rounded w-32" />
      <div className="h-4 bg-gray-100 rounded w-24" />
      <div className="h-4 bg-gray-100 rounded w-20" />
    </div>
    <div className="h-20 bg-gray-50 rounded-xl mb-6" />
    <div className="flex justify-between items-center">
      <div className="h-7 bg-gray-100 rounded-full w-36" />
      <div className="h-10 bg-gray-200 rounded-xl w-28" />
    </div>
  </div>
);

const OpportunityFeed = ({ userRole }) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedType, setSelectedType] = useState('All');
  const [filterOpen, setFilterOpen] = useState(false);
  const [page, setPage] = useState(1);
  const LIMIT = 10;

  const [detailOpportunity, setDetailOpportunity] = useState(null);
  const [applyOpportunity, setApplyOpportunity] = useState(null);
  const [submittedOpportunity, setSubmittedOpportunity] = useState(null);
  const [showPostModal, setShowPostModal] = useState(false);

  const filters = {
    ...(selectedType !== 'All' ? { type: selectedType } : {}),
    ...(searchQuery ? { q: searchQuery } : {}),
    page,
    limit: LIMIT,
  };
  const { data, isLoading, isError } = useOpportunities(filters);

  const opportunities = data?.data?.data || data?.data || [];
  const meta = data?.data?.meta || {};

  const filtered = opportunities;

  // ── Application success screen ───────────────────────────────────────────────
  if (submittedOpportunity) {
    return (
      <div className="flex-1 flex items-center justify-center py-12 px-4">
        <div className="bg-white rounded-2xl border border-gray-200 p-12 max-w-xl w-full text-center shadow-sm">
          <div className="w-16 h-16 bg-[#e8fbf2] rounded-full flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-[#10b981]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-3">Application Submitted!</h2>
          <p className="text-gray-600 mb-8 leading-relaxed">
            Your application for <span className="font-semibold break-words [overflow-wrap:anywhere]">{submittedOpportunity.title}</span> at{' '}
            <span className="font-semibold break-words [overflow-wrap:anywhere]">{submittedOpportunity.clinicName}</span> has been submitted.
          </p>
          <div className="bg-[#eff6ff] border border-blue-100 rounded-xl p-4 mb-8">
            <p className="text-sm text-blue-800 leading-relaxed font-medium">
              The hiring team will review your application and clinical response. You'll be notified of the next steps.
            </p>
          </div>
          <div className="flex justify-center gap-4">
            <button
              onClick={() => setSubmittedOpportunity(null)}
              className="px-6 py-2.5 text-sm font-semibold text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors"
            >
              Back to Opportunities
            </button>
            <button
              onClick={() => { setSubmittedOpportunity(null); navigate('/my-applications'); }}
              className="px-6 py-2.5 text-sm font-semibold text-white bg-[#4f46e5] rounded-xl hover:bg-[#4338ca] transition-colors shadow-sm"
            >
              View My Applications
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 max-w-5xl mx-auto w-full px-8 py-10">
      {!detailOpportunity && !applyOpportunity && (
        <>
          {/* Page header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 mb-1">Veterinary Opportunities</h1>
          <p className="text-sm text-gray-500">Apply to positions and showcase your clinical thinking</p>
        </div>
        {/* Post Job button for doctors */}
        {userRole === 'veterinarian' && (
          <button
            id="post-job-btn"
            onClick={() => setShowPostModal(true)}
            className="flex items-center gap-2 px-5 py-2.5 text-sm font-semibold text-white bg-[#6b46c1] rounded-xl hover:bg-[#553098] transition-colors shadow-sm"
          >
            <Plus size={16} /> Post a Job
          </button>
        )}
      </div>

      {/* Search + Filter bar */}
      <div className="flex items-center gap-3 mb-6">
        {/* Search */}
        <div className="relative flex-1 max-w-md">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => {
              setSearchQuery(e.target.value);
              setPage(1);
            }}
            placeholder="Search by title, clinic, or location…"
            className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#6b46c1]/30 focus:border-[#6b46c1] transition-colors placeholder:text-gray-400"
          />
        </div>

        {/* Filter dropdown (React state, no DOM hacks) */}
        <div className="relative">
          <button
            onClick={() => setFilterOpen((prev) => !prev)}
            className="flex items-center gap-2 px-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors shadow-sm"
          >
            <SlidersHorizontal size={16} className="text-gray-500" />
            {selectedType !== 'All' ? selectedType : 'Filters'}
            {selectedType !== 'All' && (
              <span className="w-2 h-2 bg-[#6b46c1] rounded-full" />
            )}
          </button>

          {filterOpen && (
            <>
              <div className="fixed inset-0 z-10" onClick={() => setFilterOpen(false)} />
              <div className="absolute top-full left-0 mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-20">
                {TYPES.map((t) => (
                  <button
                    key={t}
                    onClick={() => {
                      setSelectedType(t);
                      setPage(1);
                      setFilterOpen(false);
                    }}
                    className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                      selectedType === t
                        ? 'bg-[#faf5ff] text-[#6b46c1] font-semibold'
                        : 'text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Results count */}
      {!isLoading && (
        <p className="text-xs text-gray-400 mb-4 font-medium">
          {searchQuery
            ? `${filtered.length} result${filtered.length !== 1 ? 's' : ''} for "${searchQuery}"`
            : meta.total
            ? `${meta.total} opportunit${meta.total !== 1 ? 'ies' : 'y'}`
            : `${opportunities.length} opportunit${opportunities.length !== 1 ? 'ies' : 'y'}`}
          {selectedType !== 'All' && ` · ${selectedType}`}
        </p>
      )}

      {/* Loading skeletons */}
      {isLoading && (
        <div className="space-y-6">
          {[1, 2, 3].map((i) => <OpportunityCardSkeleton key={i} />)}
        </div>
      )}

      {/* Error */}
      {isError && !isLoading && (
        <div className="text-center py-16 text-red-500 text-sm">
          Error loading opportunities. Please try again.
        </div>
      )}

      {/* Cards */}
      {!isLoading && !isError && (
        <>
          {filtered.length === 0 ? (
            <div className="text-center py-16">
              <Briefcase size={40} className="text-gray-200 mx-auto mb-3" />
              <p className="text-gray-700 font-medium mb-1">No opportunities found</p>
              <p className="text-sm text-gray-400">
                {searchQuery ? `No results for "${searchQuery}"` : selectedType !== 'All' ? `No ${selectedType} positions right now` : 'Check back soon!'}
              </p>
              {(searchQuery || selectedType !== 'All') && (
                <button
                  onClick={() => {
                    setSearchQuery('');
                    setSelectedType('All');
                  }}
                  className="mt-4 text-sm font-semibold text-[#6b46c1] hover:underline"
                >
                  Clear filters
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-6">
              {filtered.map((opp) => (
                <OpportunityCard
                  key={opp.id}
                  opportunity={opp}
                  userRole={userRole}
                  onViewDetail={(o) => setDetailOpportunity(o)}
                  onApply={(o) => setApplyOpportunity(o)}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {!searchQuery && meta.totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 mt-10">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                ← Previous
              </button>
              <span className="text-sm text-gray-500 font-medium">
                Page {page} of {meta.totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(meta.totalPages, p + 1))}
                disabled={page === meta.totalPages}
                className="px-4 py-2 text-sm font-semibold text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}
        </>
      )}

      {/* Modals */}
      <OpportunityDetailModal
        isOpen={!!detailOpportunity}
        onClose={() => setDetailOpportunity(null)}
        opportunity={detailOpportunity}
        userRole={userRole}
        onApply={(opp) => { setDetailOpportunity(null); setApplyOpportunity(opp); }}
      />
      <ApplyModal
        isOpen={!!applyOpportunity}
        onClose={() => setApplyOpportunity(null)}
        opportunity={applyOpportunity}
        onApplySuccess={(opp) => { setApplyOpportunity(null); setSubmittedOpportunity(opp); }}
      />
      <PostOpportunityModal
        isOpen={showPostModal}
        onClose={() => setShowPostModal(false)}
      />
    </div>
  );
};

export default OpportunityFeed;
